import { HttpBackend, HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, catchError, debounceTime, filter, first, forkJoin, Observable, of, switchMap, tap } from 'rxjs';
import { AuthService } from '../auth/auth.service';
import { SelectedCustomerService } from '../selected-customer/selected-customer.service';
import { BackendFeatureService, GungFeature } from '../backend-feature.service';
import { GungCommonEventSystemService, GungEvent } from 'gung-common';

@Injectable({
  providedIn: 'root'
})
export class GungEventSystemService extends GungCommonEventSystemService {
  protected translateService = inject(TranslateService);
  protected httpClient = inject(HttpClient);
  protected httpBackend = inject(HttpBackend);
  protected authService = inject(AuthService);
  protected selectedCustomerService = inject(SelectedCustomerService);
  protected backendFeatureService = inject(BackendFeatureService);

  protected minimumBaseVersion: string = '4.0.5-SNAPSHOT';

  private httpNoInterceptor = new HttpClient(this.httpBackend);
  private currentFrontendVersion: string | null = null;

  protected gungEventQueueDebounceTime: number = 5000;
  protected gungEventQueue: BehaviorSubject<GungEvent[]> = new BehaviorSubject<GungEvent[]>([]);

  constructor() {
    super();
    this.gungEventQueue.pipe(
      debounceTime(this.gungEventQueueDebounceTime),
      filter(queue => queue.length > 0),
      switchMap(queue => this.httpClient.post<any>('json/events/collect', queue).pipe(first(), catchError(() => of(undefined)))),
      tap(() => this.setEventQueue([]))
    ).subscribe(() => { });
  }

  protected setEventQueue(gungEvents: GungEvent[]): void {
    this.gungEventQueue.next(gungEvents);
  }

  /**
 * Gets the current frontend version from the backend using an HTTP request.
 * The version is derived from the ETag header and cached for subsequent calls.
 *
 * @private
 * @returns An `Observable` that emits the current frontend version string or `null` if unavailable.
 */
  private getCurrentFrontendVersion(): Observable<string | null> {
    if (!!this.currentFrontendVersion) {
      return of(this.currentFrontendVersion);
    }

    const url = `${window.location.origin}?cacheBuster=${new Date().getTime()}`;
    return this.httpNoInterceptor.get(url, {
      observe: 'response',
      responseType: 'text',
      headers: {
        maxAge: '-1'
      }
    }).pipe(
      catchError(() => {
        // Handle the error and return an empty response or a default value
        return of(new HttpResponse<string>({ body: '', headers: new HttpHeaders() }));
      }),
      switchMap((response: HttpResponse<string>) => {
        const etag = response.headers.get('Etag');
        this.currentFrontendVersion = etag;
        return of(etag);
      })
    );
  }

  private parseBaseVersionIdToIntArray(version: string): number[] {
    const splitted = version.split('-');
    const numberPartSplitted = splitted[0].split('.');
    return [parseInt(numberPartSplitted[0]), parseInt(numberPartSplitted[1]), parseInt(numberPartSplitted[2])];
  }

  private baseVersionMatching(baseVersion: GungFeature, minimumBaseVersion: string) {
    if (!baseVersion) {
      return false;
    }

    const base = this.parseBaseVersionIdToIntArray(baseVersion.version);
    const minimum = this.parseBaseVersionIdToIntArray(minimumBaseVersion);

    const allowedByMajor = base[0] > minimum[0];
    if (allowedByMajor) {
      return true;
    }

    const allowedByMinor = base[0] >= minimum[0] && base[1] > minimum[1];
    if (allowedByMinor) {
      return true;
    }

    const allowedByPatch = base[0] >= minimum[0] && base[1] >= minimum[1] && base[2] >= minimum[2];
    if (allowedByPatch) {
      return true;
    }

    return false;
  }

  /**
   * Gets the current browser session ID from session storage.
   * Generates and stores a new UUID if none exists.
   *
   * @private
   * @returns The browser session identifier string.
   */
  private getBrowserSessionId(): string {
    const browserSessionId = sessionStorage.getItem('GungBrowserSessionId');
    if (!!browserSessionId) {
      return browserSessionId;
    } else {
      const newBrowserSessionId = crypto.randomUUID().toString();
      sessionStorage.setItem('GungBrowserSessionId', newBrowserSessionId);
      return newBrowserSessionId;
    }
  }

  /**
   * Initializes a new `GungEvent` object with default values such as ID, timestamp, and metadata.
   *
   * @private
   * @returns An `Observable` that emits a new `GungEvent` object with base defaults.
   */
  private initializeGungEvent(): Observable<GungEvent> {
    return of({
      id: crypto.randomUUID().toString(),
      eventType: '',
      eventVersion: 1,
      occuredAt: new Date().toISOString(),
      source: 'web',
      customerId: '',
      userId: '',
      userRoles: [],
      sessionId: '',
      clientContext: {},
      payload: {}
    });
  }

  /**
   * Gets the mapping of Gung event types to their current version numbers.
   * Used to maintain backward compatibility and version control for emitted events.
   *
   * @protected
   * @returns An object where each key is a Gung event type and the value is its version number.
   */
  protected getGungEventTypeVersions(): { [eventType: string]: number } {
    return {
      "cartQuantityChange": 1,
      "listFiltered": 1,
      "productView": 1,
      "pageView": 1,
    };
  }


  /**
   * Sets default user, customer, and session information on a `GungEvent`.
   * Fetches current user and selected customer asynchronously before populating the event.
   *
   * @protected
   * @param gungEvent The base `GungEvent` to update.
   * @returns An `Observable` that emits the updated `GungEvent`.
   */
  protected setGungEventDefaults(gungEvent: GungEvent): Observable<GungEvent> {
    return forkJoin({
      currentUser: this.authService.getCurrentUser().pipe(filter(user => !!user), first()),
      selectedCustomer: this.selectedCustomerService.getSelectedCustomer().pipe(filter(customer => !!customer), first()),
    }).pipe(
      catchError(() => of({ currentUser: undefined, selectedCustomer: undefined })),
      switchMap(({ currentUser, selectedCustomer }) => {
        const newGungEvent: GungEvent = structuredClone(gungEvent);

        newGungEvent.customerId = selectedCustomer?.id || '';
        newGungEvent.userId = currentUser?.username || '';
        newGungEvent.userRoles = currentUser?.roles || '';
        newGungEvent.sessionId = this.getBrowserSessionId();

        return of(newGungEvent);
      })
    );
  }

  /**
   * Adds client context details such as user agent, origin, language, and frontend version
   * to a given `GungEvent`.
   *
   * @protected
   * @param gungEvent The base `GungEvent` to update.
   * @returns An `Observable` that emits the updated `GungEvent`.
   */
  protected setGungEventClientContext(gungEvent: GungEvent): Observable<GungEvent> {
    const context: { [s: string]: any } = {};

    context.userAgent = navigator.userAgent;
    context.origin = window.location.hostname;
    context.language = this.translateService.currentLang;

    return forkJoin({
      context: of(context),
      frontendVersion: this.getCurrentFrontendVersion().pipe(first())
    }).pipe(
      switchMap(({ context, frontendVersion }) => {
        context.frontendVersion = frontendVersion;

        const newGungEvent: GungEvent = structuredClone(gungEvent);
        newGungEvent.clientContext = context;

        return of(newGungEvent);
      })
    );
  }

  /**
   * Sets the event type and payload on a `GungEvent` object.
   *
   * @protected
   * @param gungEvent The base `GungEvent` to update.
   * @param eventType The event type string or `GungEventType`.
   * @param eventPayload The payload object containing event details.
   * @param overrideEventVersion Optional parameter that overrides the version of the current event.
   * @returns An `Observable` that emits the updated `GungEvent`.
   */
  protected setGungEventTypeAndPayload(
    gungEvent: GungEvent,
    eventType: string,
    eventPayload: { [s: string]: any },
    overrideEventVersion?: number
  ): Observable<GungEvent> {
    const newGungEvent: GungEvent = structuredClone(gungEvent);

    newGungEvent.eventType = eventType;
    newGungEvent.payload = eventPayload;

    /* Prioritize the overrideEventVersion and then the event version map */
    if (!!overrideEventVersion) {
      newGungEvent.eventVersion = overrideEventVersion;
    } else if (!!this.getGungEventTypeVersions()[eventType]) {
      newGungEvent.eventVersion = this.getGungEventTypeVersions()[eventType];
    }

    return of(newGungEvent);
  }

  /**
   * Builds a complete `GungEvent` object by sequentially setting defaults,
   * client context, event type, and payload.
   *
   * @private
   * @param eventType The type of event being created.
   * @param eventPayload The data associated with the event.
   * @param overrideEventVersion Optional parameter that overrides the version of the current event.
   * @returns An `Observable` that emits the fully constructed `GungEvent`.
   */
  private createGungEventObject(
    eventType: string,
    eventPayload: { [s: string]: any },
    overrideEventVersion?: number
  ): Observable<GungEvent> {
    return this.initializeGungEvent().pipe(
      first(),
      switchMap(gungEvent => this.setGungEventDefaults(gungEvent).pipe(first())),
      switchMap(gungEvent => this.setGungEventClientContext(gungEvent).pipe(first())),
      switchMap(gungEvent => this.setGungEventTypeAndPayload(gungEvent, eventType, eventPayload, overrideEventVersion).pipe(first()))
    );
  }

  /**
   * Emits a `GungEvent` to the backend telemetry system.
   * Creates the full event object with metadata and sends it to the backend.
   *()
   * @public
   * @param eventType The type of event to emit.
   * @param eventPayload The payload containing event-specific information.
   * @param overrideEventVersion Optional parameter that overrides the version of the current event.
   * @returns void
   */
  public emitGungEvent(eventType: string, eventPayload: { [s: string]: any }, overrideEventVersion?: number): void {
    this.backendFeatureService.getAvailableFeatures().pipe(
      catchError(() => of({})),
      switchMap(features => {
        if (this.baseVersionMatching(features['gung'], this.minimumBaseVersion)) {
          return this.createGungEventObject(eventType, eventPayload, overrideEventVersion).pipe(first());
        } else {
          return of(undefined);
        }
      }),
      switchMap(gungEvent => {
        if (!!gungEvent) {
          this.setEventQueue([...this.gungEventQueue.value, gungEvent]);
        }
        return of(undefined);
      })
    ).subscribe(() => { });
  }

  /**
   * Emits a `GungEvent` to the backend telemetry system.
   * Creates the full event object with metadata and sends it to the backend.
   *
   * @public
   * @param eventType The type of event to emit.
   * @param eventPayload The payload containing event-specific information.
   * @param overrideEventVersion Optional parameter that overrides the version of the current event.
   * @returns void
   */
  public emitMultipleGungEvents(gungEvents: { eventType: string, eventPayload: { [s: string]: any }, overrideEventVersion?: number }[]): void {
    this.backendFeatureService.getAvailableFeatures().pipe(
      catchError(() => of({})),
      switchMap(features => {
        if (this.baseVersionMatching(features['gung'], this.minimumBaseVersion)) {
          return forkJoin(gungEvents.map(ge => this.createGungEventObject(ge.eventType, ge.eventPayload, ge.overrideEventVersion).pipe(first())))
        } else {
          return of(undefined);
        }
      }),
      switchMap(gungEvents => {
        if (!!gungEvents) {
          this.setEventQueue([...this.gungEventQueue.value, ...gungEvents]);
        }
        return of(undefined);
      })
    ).subscribe(() => { });
  }
}
